"use client";

import { c as _c } from "react/compiler-runtime";
import React, { KeyboardEvent, useRef, useCallback, useEffect, useState } from "react";
import TextConversionButtons from "./TextConversionButtons";
import ModelDropdown from "./ModelDropdown";
import WorkflowSelector from "./WorkflowSelector";
import StepByStepToggle from "./StepByStepToggle";
import MicrophoneButton from "./MicrophoneButton";
import { ChatbotSelector } from "@/app/chatbots/components";
import type { AIModel } from "@/lib/apiKeyStorage";
import type { ExposedWorkflow } from "@/app/canvas/features/master-trigger/types";
import type { FeatureId } from "@/types/features";
import type { Chatbot } from "@/types/chatbot";
import { getFileUploadWarning } from "@/lib/modelCapabilities";
import { quickValidateFile } from "@/lib/fileUploadSecurity";
import { useResizableComposer } from "@/hooks/useResizableComposer";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useVoiceActivityDetection } from "@/hooks/useVoiceActivityDetection";
import { useAutoSendDetection } from "@/hooks/useAutoSendDetection";
import { usePushToTalk } from "@/hooks/usePushToTalk";
type MessageComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  selectedModel?: AIModel;
  onModelChange?: (model: AIModel) => void;
  onSummarize?: () => void;
  onSummarizeAndContinue?: () => void;
  onFork?: () => void;
  canSummarize?: boolean;
  summarizing?: boolean;
  summarizingAndContinuing?: boolean;
  forking?: boolean;
  attachedFiles?: File[];
  onFilesChange?: (files: File[]) => void;
  onConvertToMarkdown?: () => void;
  onConvertToJson?: () => void;
  convertingToMarkdown?: boolean;
  convertingToJson?: boolean;
  // Step-by-step mode props
  isStepByStepWithExplanation?: boolean;
  isStepByStepNoExplanation?: boolean;
  onToggleStepByStepWithExplanation?: () => void;
  onToggleStepByStepNoExplanation?: () => void;
  // Web search toggle
  enableWebSearch?: boolean;
  onToggleWebSearch?: () => void;
  webSearchDisabled?: boolean; // True when model doesn't support web search
  userTier?: "trial" | "pro" | "expired" | "pending";
  isFeatureEnabled?: (featureId: FeatureId) => boolean;
  // Workflow selection props
  workflows?: ExposedWorkflow[];
  selectedWorkflow?: ExposedWorkflow | null;
  onWorkflowChange?: (workflow: ExposedWorkflow | null) => void;
  workflowsLoading?: boolean;
  workflowExecuting?: boolean;
  // Chatbot selection props
  chatbots?: Chatbot[];
  selectedChatbot?: Chatbot | null;
  onChatbotChange?: (chatbot: Chatbot | null) => void;
  chatbotsLoading?: boolean;
};
const MessageComposer: React.FC<MessageComposerProps> = t0 => {
  const $ = _c(206);
  const {
    value,
    onChange,
    onSend,
    disabled,
    selectedModel,
    onModelChange,
    onSummarize,
    onSummarizeAndContinue,
    onFork,
    canSummarize: t1,
    summarizing: t2,
    summarizingAndContinuing: t3,
    forking: t4,
    attachedFiles: t5,
    onFilesChange,
    onConvertToMarkdown,
    onConvertToJson,
    convertingToMarkdown: t6,
    convertingToJson: t7,
    isStepByStepWithExplanation: t8,
    isStepByStepNoExplanation: t9,
    onToggleStepByStepWithExplanation,
    onToggleStepByStepNoExplanation,
    enableWebSearch: t10,
    onToggleWebSearch,
    webSearchDisabled: t11,
    userTier,
    isFeatureEnabled,
    workflows: t12,
    selectedWorkflow: t13,
    onWorkflowChange,
    workflowsLoading: t14,
    workflowExecuting: t15,
    chatbots: t16,
    selectedChatbot: t17,
    onChatbotChange,
    chatbotsLoading: t18
  } = t0;
  const canSummarize = t1 === undefined ? false : t1;
  const summarizing = t2 === undefined ? false : t2;
  const summarizingAndContinuing = t3 === undefined ? false : t3;
  const forking = t4 === undefined ? false : t4;
  let t19;
  if ($[0] !== t5) {
    t19 = t5 === undefined ? [] : t5;
    $[0] = t5;
    $[1] = t19;
  } else {
    t19 = $[1];
  }
  const attachedFiles = t19;
  const convertingToMarkdown = t6 === undefined ? false : t6;
  const convertingToJson = t7 === undefined ? false : t7;
  const isStepByStepWithExplanation = t8 === undefined ? false : t8;
  const isStepByStepNoExplanation = t9 === undefined ? false : t9;
  const enableWebSearch = t10 === undefined ? true : t10;
  const webSearchDisabled = t11 === undefined ? false : t11;
  let t20;
  if ($[2] !== t12) {
    t20 = t12 === undefined ? [] : t12;
    $[2] = t12;
    $[3] = t20;
  } else {
    t20 = $[3];
  }
  const workflows = t20;
  const selectedWorkflow = t13 === undefined ? null : t13;
  const workflowsLoading = t14 === undefined ? false : t14;
  const workflowExecuting = t15 === undefined ? false : t15;
  let t21;
  if ($[4] !== t16) {
    t21 = t16 === undefined ? [] : t16;
    $[4] = t16;
    $[5] = t21;
  } else {
    t21 = $[5];
  }
  const chatbots = t21;
  const selectedChatbot = t17 === undefined ? null : t17;
  const chatbotsLoading = t18 === undefined ? false : t18;
  const fileInputRef = useRef(null);
  let t22;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = [];
    $[6] = t22;
  } else {
    t22 = $[6];
  }
  const [securityErrors, setSecurityErrors] = useState(t22);
  let t23;
  if ($[7] !== isFeatureEnabled) {
    t23 = isFeatureEnabled ? isFeatureEnabled("voice_input") : true;
    $[7] = isFeatureEnabled;
    $[8] = t23;
  } else {
    t23 = $[8];
  }
  const voiceInputEnabled = t23;
  let t24;
  if ($[9] !== isFeatureEnabled) {
    t24 = isFeatureEnabled ? isFeatureEnabled("auto_voice_detection") : false;
    $[9] = isFeatureEnabled;
    $[10] = t24;
  } else {
    t24 = $[10];
  }
  const autoVoiceDetectionEnabled = t24;
  let t25;
  if ($[11] !== isFeatureEnabled) {
    t25 = isFeatureEnabled ? isFeatureEnabled("push_to_talk") : false;
    $[11] = isFeatureEnabled;
    $[12] = t25;
  } else {
    t25 = $[12];
  }
  const pushToTalkFeatureEnabled = t25;
  let t26;
  if ($[13] !== isFeatureEnabled) {
    t26 = isFeatureEnabled ? isFeatureEnabled("file_attachments") : true;
    $[13] = isFeatureEnabled;
    $[14] = t26;
  } else {
    t26 = $[14];
  }
  const fileAttachmentsEnabled = t26;
  let t27;
  if ($[15] !== isFeatureEnabled) {
    t27 = isFeatureEnabled ? isFeatureEnabled("model_selection") : true;
    $[15] = isFeatureEnabled;
    $[16] = t27;
  } else {
    t27 = $[16];
  }
  const modelSelectionEnabled = t27;
  let t28;
  if ($[17] !== isFeatureEnabled) {
    t28 = isFeatureEnabled ? isFeatureEnabled("canvas_selector") : true;
    $[17] = isFeatureEnabled;
    $[18] = t28;
  } else {
    t28 = $[18];
  }
  const canvasSelectorEnabled = t28;
  let t29;
  if ($[19] !== isFeatureEnabled) {
    t29 = isFeatureEnabled ? isFeatureEnabled("step_by_step_mode") : true;
    $[19] = isFeatureEnabled;
    $[20] = t29;
  } else {
    t29 = $[20];
  }
  const stepByStepModeEnabled = t29;
  let t30;
  if ($[21] !== isFeatureEnabled) {
    t30 = isFeatureEnabled ? isFeatureEnabled("convert_to_markdown") : true;
    $[21] = isFeatureEnabled;
    $[22] = t30;
  } else {
    t30 = $[22];
  }
  const convertToMarkdownEnabled = t30;
  let t31;
  if ($[23] !== isFeatureEnabled) {
    t31 = isFeatureEnabled ? isFeatureEnabled("convert_to_json") : true;
    $[23] = isFeatureEnabled;
    $[24] = t31;
  } else {
    t31 = $[24];
  }
  const convertToJsonEnabled = t31;
  let t32;
  if ($[25] !== isFeatureEnabled) {
    t32 = isFeatureEnabled ? isFeatureEnabled("summarize_thread") : true;
    $[25] = isFeatureEnabled;
    $[26] = t32;
  } else {
    t32 = $[26];
  }
  const summarizeThreadEnabled = t32;
  let t33;
  if ($[27] !== isFeatureEnabled) {
    t33 = isFeatureEnabled ? isFeatureEnabled("summarize_and_continue") : true;
    $[27] = isFeatureEnabled;
    $[28] = t33;
  } else {
    t33 = $[28];
  }
  const summarizeAndContinueEnabled = t33;
  let t34;
  if ($[29] !== isFeatureEnabled) {
    t34 = isFeatureEnabled ? isFeatureEnabled("fork_thread") : true;
    $[29] = isFeatureEnabled;
    $[30] = t34;
  } else {
    t34 = $[30];
  }
  const forkThreadEnabled = t34;
  let t35;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      minHeight: 80,
      maxHeight: 600,
      initialHeight: 80
    };
    $[31] = t35;
  } else {
    t35 = $[31];
  }
  const {
    height,
    isDragging,
    handleDragStart
  } = useResizableComposer(t35);
  const valueRef = useRef(value);
  let t36;
  let t37;
  if ($[32] !== value) {
    t36 = () => {
      valueRef.current = value;
    };
    t37 = [value];
    $[32] = value;
    $[33] = t36;
    $[34] = t37;
  } else {
    t36 = $[33];
    t37 = $[34];
  }
  useEffect(t36, t37);
  let t38;
  if ($[35] !== onChange) {
    t38 = text => {
      const currentValue = valueRef.current;
      const newValue = currentValue ? `${currentValue} ${text}` : text;
      onChange(newValue);
    };
    $[35] = onChange;
    $[36] = t38;
  } else {
    t38 = $[36];
  }
  const handleTranscript = t38;
  let t39;
  if ($[37] !== handleTranscript) {
    t39 = {
      onTranscript: handleTranscript,
      continuous: true,
      interimResults: true
    };
    $[37] = handleTranscript;
    $[38] = t39;
  } else {
    t39 = $[38];
  }
  const {
    isListening,
    isSupported,
    error: speechError,
    toggleListening,
    transcript,
    startListening,
    stopListening
  } = useSpeechRecognition(t39);
  let t40;
  if ($[39] !== isListening || $[40] !== startListening || $[41] !== voiceInputEnabled) {
    t40 = () => {
      console.log("[Push-to-Talk] Key pressed - starting voice input");
      if (!isListening && voiceInputEnabled) {
        startListening();
      }
    };
    $[39] = isListening;
    $[40] = startListening;
    $[41] = voiceInputEnabled;
    $[42] = t40;
  } else {
    t40 = $[42];
  }
  let t41;
  if ($[43] !== isListening || $[44] !== stopListening) {
    t41 = () => {
      console.log("[Push-to-Talk] Key released - stopping voice input");
      if (isListening) {
        stopListening();
      }
    };
    $[43] = isListening;
    $[44] = stopListening;
    $[45] = t41;
  } else {
    t41 = $[45];
  }
  const t42 = !voiceInputEnabled || !pushToTalkFeatureEnabled || autoVoiceDetectionEnabled;
  let t43;
  if ($[46] !== t40 || $[47] !== t41 || $[48] !== t42) {
    t43 = {
      onPushStart: t40,
      onPushEnd: t41,
      disabled: t42
    };
    $[46] = t40;
    $[47] = t41;
    $[48] = t42;
    $[49] = t43;
  } else {
    t43 = $[49];
  }
  const {
    isPushing,
    isEnabled: isPushToTalkStorageEnabled
  } = usePushToTalk(t43);
  const isPushToTalkEnabled = pushToTalkFeatureEnabled && isPushToTalkStorageEnabled;
  const t44 = autoVoiceDetectionEnabled && !isListening && !isPushToTalkEnabled;
  let t45;
  if ($[50] !== isListening || $[51] !== startListening) {
    t45 = () => {
      console.log("[Auto Voice] User started speaking - activating microphone");
      if (!isListening) {
        startListening();
      }
    };
    $[50] = isListening;
    $[51] = startListening;
    $[52] = t45;
  } else {
    t45 = $[52];
  }
  let t46;
  if ($[53] !== t44 || $[54] !== t45) {
    t46 = {
      enabled: t44,
      onSpeakingStart: t45,
      onSpeakingStop: _temp
    };
    $[53] = t44;
    $[54] = t45;
    $[55] = t46;
  } else {
    t46 = $[55];
  }
  useVoiceActivityDetection(t46);
  const t47 = autoVoiceDetectionEnabled && isListening;
  const t48 = transcript || "";
  let t49;
  if ($[56] !== attachedFiles.length || $[57] !== onChange || $[58] !== onSend || $[59] !== stopListening || $[60] !== value) {
    t49 = () => {
      console.log("[Auto Voice] Send command detected - submitting message");
      const triggerWords = ["send", "submit", "send it", "submit it"];
      let cleanedValue = value;
      triggerWords.forEach(word => {
        const regex = new RegExp(`\\s*${word}[.!?]?\\s*$`, "i");
        cleanedValue = cleanedValue.replace(regex, "").trim();
      });
      if (cleanedValue !== value) {
        onChange(cleanedValue);
      }
      stopListening();
      setTimeout(() => {
        if (cleanedValue.trim() || attachedFiles.length > 0) {
          onSend();
        }
      }, 100);
    };
    $[56] = attachedFiles.length;
    $[57] = onChange;
    $[58] = onSend;
    $[59] = stopListening;
    $[60] = value;
    $[61] = t49;
  } else {
    t49 = $[61];
  }
  let t50;
  if ($[62] !== t47 || $[63] !== t48 || $[64] !== t49) {
    t50 = {
      enabled: t47,
      transcript: t48,
      onSendDetected: t49
    };
    $[62] = t47;
    $[63] = t48;
    $[64] = t49;
    $[65] = t50;
  } else {
    t50 = $[65];
  }
  useAutoSendDetection(t50);
  let t51;
  if ($[66] !== attachedFiles.length || $[67] !== disabled || $[68] !== onSend || $[69] !== value) {
    t51 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!disabled && (value.trim() || attachedFiles.length > 0)) {
          onSend();
        }
      }
    };
    $[66] = attachedFiles.length;
    $[67] = disabled;
    $[68] = onSend;
    $[69] = value;
    $[70] = t51;
  } else {
    t51 = $[70];
  }
  const handleKeyDown = t51;
  let t52;
  if ($[71] !== attachedFiles || $[72] !== onFilesChange) {
    t52 = e_0 => {
      if (e_0.target.files && e_0.target.files.length > 0 && onFilesChange) {
        const newFiles = Array.from(e_0.target.files);
        const validFiles = [];
        const errors = [];
        for (const file of newFiles) {
          const validation = quickValidateFile(file);
          if (validation.valid) {
            validFiles.push(file);
          } else {
            errors.push(`${file.name}: ${validation.error}`);
          }
        }
        if (errors.length > 0) {
          setSecurityErrors(errors);
          setTimeout(() => setSecurityErrors([]), 10000);
        }
        if (validFiles.length > 0) {
          onFilesChange([...attachedFiles, ...validFiles]);
        }
        e_0.target.value = "";
      }
    };
    $[71] = attachedFiles;
    $[72] = onFilesChange;
    $[73] = t52;
  } else {
    t52 = $[73];
  }
  const handleFileSelect = t52;
  let t53;
  if ($[74] !== attachedFiles || $[75] !== onFilesChange) {
    t53 = index => {
      if (onFilesChange) {
        const newFiles_0 = attachedFiles.filter((_, i) => i !== index);
        onFilesChange(newFiles_0);
      }
    };
    $[74] = attachedFiles;
    $[75] = onFilesChange;
    $[76] = t53;
  } else {
    t53 = $[76];
  }
  const handleRemoveFile = t53;
  const formatFileSize = _temp2;
  let t54;
  if ($[77] !== attachedFiles || $[78] !== selectedModel) {
    t54 = selectedModel ? getFileUploadWarning(selectedModel, attachedFiles) : null;
    $[77] = attachedFiles;
    $[78] = selectedModel;
    $[79] = t54;
  } else {
    t54 = $[79];
  }
  const fileWarning = t54;
  let t55;
  if ($[80] !== securityErrors) {
    t55 = securityErrors.length > 0 && <div className="mb-3 rounded-md border border-red-600 bg-red-600/10 px-3 py-2 text-xs text-red-400"><div className="flex items-start gap-2"><svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg><div className="flex flex-col gap-1"><span className="font-medium">File(s) blocked for security:</span>{securityErrors.map(_temp3)}</div><button onClick={() => setSecurityErrors([])} className="ml-auto p-1 hover:bg-red-600/20 rounded" title="Dismiss"><svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div></div>;
    $[80] = securityErrors;
    $[81] = t55;
  } else {
    t55 = $[81];
  }
  let t56;
  if ($[82] !== fileWarning) {
    t56 = fileWarning && <div className="mb-3 rounded-md border border-amber-600 bg-amber-600/10 px-3 py-2 text-xs text-amber-400"><div className="flex items-start gap-2"><svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg><span>{fileWarning}</span></div></div>;
    $[82] = fileWarning;
    $[83] = t56;
  } else {
    t56 = $[83];
  }
  let t57;
  if ($[84] !== attachedFiles || $[85] !== handleRemoveFile) {
    t57 = attachedFiles.length > 0 && <div className="mb-3 flex flex-wrap gap-2">{attachedFiles.map((file_0, index_1) => <div key={index_1} className="flex items-center gap-2 rounded-md border border-white/40 bg-white/60 px-3 py-2 text-xs"><div className="flex items-center gap-2">{file_0.type.startsWith("image/") ? <svg className="h-4 w-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> : <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}<div className="flex flex-col"><span className="text-foreground font-medium">{file_0.name}</span><span className="text-foreground-secondary/60">{formatFileSize(file_0.size)}</span></div></div><button onClick={() => handleRemoveFile(index_1)} className="ml-2 rounded-md p-1 text-foreground-secondary/40 hover:bg-white/40 hover:text-foreground" title="Remove file"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div>)}</div>;
    $[84] = attachedFiles;
    $[85] = handleRemoveFile;
    $[86] = t57;
  } else {
    t57 = $[86];
  }
  const t58 = `group flex items-center justify-center h-2 cursor-ns-resize hover:bg-white/60 rounded-t transition-colors ${isDragging ? "bg-white/80" : ""}`;
  let t59;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <div className="flex gap-1 opacity-40 group-hover:opacity-70 transition-opacity"><div className="w-1 h-1 rounded-full bg-foreground/40" /><div className="w-1 h-1 rounded-full bg-foreground/40" /><div className="w-1 h-1 rounded-full bg-foreground/40" /></div>;
    $[87] = t59;
  } else {
    t59 = $[87];
  }
  let t60;
  if ($[88] !== handleDragStart || $[89] !== t58) {
    t60 = <div onMouseDown={handleDragStart} className={t58} title="Drag to resize">{t59}</div>;
    $[88] = handleDragStart;
    $[89] = t58;
    $[90] = t60;
  } else {
    t60 = $[90];
  }
  const t61 = `${height}px`;
  let t62;
  if ($[91] !== t61) {
    t62 = {
      height: t61
    };
    $[91] = t61;
    $[92] = t62;
  } else {
    t62 = $[92];
  }
  let t63;
  if ($[93] !== onChange) {
    t63 = e_1 => onChange(e_1.target.value);
    $[93] = onChange;
    $[94] = t63;
  } else {
    t63 = $[94];
  }
  let t64;
  if ($[95] !== disabled || $[96] !== handleKeyDown || $[97] !== t62 || $[98] !== t63 || $[99] !== value) {
    t64 = <textarea className="resize-none rounded-md border border-white/40 bg-white/60 px-3 py-2 text-sm text-foreground placeholder:text-foreground-secondary/50 focus:outline-none focus:ring-1 focus:ring-secondary-sky shadow-inner" style={t62} placeholder={"Send a message\u2026 (Shift+Enter for new line)"} value={value} onChange={t63} onKeyDown={handleKeyDown} disabled={disabled} />;
    $[95] = disabled;
    $[96] = handleKeyDown;
    $[97] = t62;
    $[98] = t63;
    $[99] = value;
    $[100] = t64;
  } else {
    t64 = $[100];
  }
  let t65;
  if ($[101] !== chatbots || $[102] !== chatbotsLoading || $[103] !== disabled || $[104] !== onChatbotChange || $[105] !== selectedChatbot) {
    t65 = onChatbotChange && <ChatbotSelector chatbots={chatbots} selectedChatbot={selectedChatbot} onSelectChatbot={onChatbotChange} loading={chatbotsLoading} disabled={disabled} compact={true} />;
    $[101] = chatbots;
    $[102] = chatbotsLoading;
    $[103] = disabled;
    $[104] = onChatbotChange;
    $[105] = selectedChatbot;
    $[106] = t65;
  } else {
    t65 = $[106];
  }
  let t66;
  if ($[107] !== canvasSelectorEnabled || $[108] !== disabled || $[109] !== onWorkflowChange || $[110] !== selectedWorkflow || $[111] !== workflowExecuting || $[112] !== workflows || $[113] !== workflowsLoading) {
    t66 = canvasSelectorEnabled && onWorkflowChange && <WorkflowSelector workflows={workflows} selectedWorkflow={selectedWorkflow} onWorkflowChange={onWorkflowChange} isLoading={workflowsLoading} disabled={disabled || workflowExecuting} />;
    $[107] = canvasSelectorEnabled;
    $[108] = disabled;
    $[109] = onWorkflowChange;
    $[110] = selectedWorkflow;
    $[111] = workflowExecuting;
    $[112] = workflows;
    $[113] = workflowsLoading;
    $[114] = t66;
  } else {
    t66 = $[114];
  }
  let t67;
  if ($[115] !== disabled || $[116] !== modelSelectionEnabled || $[117] !== onModelChange || $[118] !== selectedModel || $[119] !== userTier) {
    t67 = modelSelectionEnabled && selectedModel && onModelChange && <ModelDropdown selectedModel={selectedModel} onModelChange={onModelChange} disabled={disabled} userTier={userTier} />;
    $[115] = disabled;
    $[116] = modelSelectionEnabled;
    $[117] = onModelChange;
    $[118] = selectedModel;
    $[119] = userTier;
    $[120] = t67;
  } else {
    t67 = $[120];
  }
  let t68;
  if ($[121] !== disabled || $[122] !== fileAttachmentsEnabled || $[123] !== handleFileSelect || $[124] !== onFilesChange) {
    t68 = fileAttachmentsEnabled && onFilesChange && <div><input ref={fileInputRef} type="file" multiple={true} onChange={handleFileSelect} className="hidden" accept="image/*,.pdf,.txt,.md,.json,.csv,.js,.ts,.tsx,.jsx,.py,.java,.c,.cpp,.html,.css,.xml,.yaml,.yml" /><button onClick={() => fileInputRef.current?.click()} disabled={disabled} className="flex items-center gap-2 rounded-md border border-foreground/30 bg-white/60 px-3 py-1.5 text-xs text-foreground hover:bg-white/80 disabled:opacity-60 transition-colors" title="Attach files or images"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>Attach files</button></div>;
    $[121] = disabled;
    $[122] = fileAttachmentsEnabled;
    $[123] = handleFileSelect;
    $[124] = onFilesChange;
    $[125] = t68;
  } else {
    t68 = $[125];
  }
  let t69;
  if ($[126] !== autoVoiceDetectionEnabled || $[127] !== disabled || $[128] !== isListening || $[129] !== isPushToTalkEnabled || $[130] !== isPushing || $[131] !== isSupported || $[132] !== speechError || $[133] !== toggleListening || $[134] !== voiceInputEnabled) {
    t69 = voiceInputEnabled && <MicrophoneButton isListening={isListening} isSupported={isSupported} onClick={toggleListening} disabled={disabled || isPushToTalkEnabled} error={speechError} autoVoiceMode={autoVoiceDetectionEnabled} pushToTalkMode={isPushToTalkEnabled} isPushing={isPushing} />;
    $[126] = autoVoiceDetectionEnabled;
    $[127] = disabled;
    $[128] = isListening;
    $[129] = isPushToTalkEnabled;
    $[130] = isPushing;
    $[131] = isSupported;
    $[132] = speechError;
    $[133] = toggleListening;
    $[134] = voiceInputEnabled;
    $[135] = t69;
  } else {
    t69 = $[135];
  }
  let t70;
  if ($[136] !== disabled || $[137] !== enableWebSearch || $[138] !== onToggleWebSearch || $[139] !== webSearchDisabled) {
    t70 = onToggleWebSearch && <button onClick={onToggleWebSearch} disabled={disabled || webSearchDisabled} className={`flex items-center gap-2 rounded-md border border-foreground/30 bg-white/60 px-3 py-1.5 text-xs text-foreground hover:bg-white/80 disabled:opacity-60 disabled:cursor-not-allowed transition-all ${webSearchDisabled ? "opacity-50" : ""}`} title={webSearchDisabled ? "Web search not available for this model" : enableWebSearch ? "Web search enabled - AI can search the web for current information" : "Web search disabled - AI will only use its training data"}><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>{webSearchDisabled ? "Web search N/A" : enableWebSearch ? "Web search ON" : "Web search OFF"}</button>;
    $[136] = disabled;
    $[137] = enableWebSearch;
    $[138] = onToggleWebSearch;
    $[139] = webSearchDisabled;
    $[140] = t70;
  } else {
    t70 = $[140];
  }
  let t71;
  if ($[141] !== t65 || $[142] !== t66 || $[143] !== t67 || $[144] !== t68 || $[145] !== t69 || $[146] !== t70) {
    t71 = <div className="flex items-center gap-2">{t65}{t66}{t67}{t68}{t69}{t70}</div>;
    $[141] = t65;
    $[142] = t66;
    $[143] = t67;
    $[144] = t68;
    $[145] = t69;
    $[146] = t70;
    $[147] = t71;
  } else {
    t71 = $[147];
  }
  let t72;
  if ($[148] !== disabled || $[149] !== isStepByStepNoExplanation || $[150] !== isStepByStepWithExplanation || $[151] !== onToggleStepByStepNoExplanation || $[152] !== onToggleStepByStepWithExplanation || $[153] !== stepByStepModeEnabled) {
    t72 = stepByStepModeEnabled && onToggleStepByStepWithExplanation && onToggleStepByStepNoExplanation && <StepByStepToggle isWithExplanationEnabled={isStepByStepWithExplanation} isNoExplanationEnabled={isStepByStepNoExplanation} onToggleWithExplanation={onToggleStepByStepWithExplanation} onToggleNoExplanation={onToggleStepByStepNoExplanation} disabled={disabled} />;
    $[148] = disabled;
    $[149] = isStepByStepNoExplanation;
    $[150] = isStepByStepWithExplanation;
    $[151] = onToggleStepByStepNoExplanation;
    $[152] = onToggleStepByStepWithExplanation;
    $[153] = stepByStepModeEnabled;
    $[154] = t72;
  } else {
    t72 = $[154];
  }
  let t73;
  if ($[155] !== convertToJsonEnabled || $[156] !== convertToMarkdownEnabled || $[157] !== convertingToJson || $[158] !== convertingToMarkdown || $[159] !== disabled || $[160] !== onConvertToJson || $[161] !== onConvertToMarkdown || $[162] !== value) {
    t73 = (convertToMarkdownEnabled || convertToJsonEnabled) && onConvertToMarkdown && onConvertToJson && <TextConversionButtons hasText={!!value.trim()} convertingToMarkdown={convertingToMarkdown} convertingToJson={convertingToJson} onConvertToMarkdown={onConvertToMarkdown} onConvertToJson={onConvertToJson} disabled={disabled} showMarkdownButton={convertToMarkdownEnabled} showJsonButton={convertToJsonEnabled} />;
    $[155] = convertToJsonEnabled;
    $[156] = convertToMarkdownEnabled;
    $[157] = convertingToJson;
    $[158] = convertingToMarkdown;
    $[159] = disabled;
    $[160] = onConvertToJson;
    $[161] = onConvertToMarkdown;
    $[162] = value;
    $[163] = t73;
  } else {
    t73 = $[163];
  }
  let t74;
  if ($[164] !== t60 || $[165] !== t64 || $[166] !== t71 || $[167] !== t72 || $[168] !== t73) {
    t74 = <div className="flex-1 flex flex-col gap-2">{t60}{t64}{t71}{t72}{t73}</div>;
    $[164] = t60;
    $[165] = t64;
    $[166] = t71;
    $[167] = t72;
    $[168] = t73;
    $[169] = t74;
  } else {
    t74 = $[169];
  }
  let t75;
  if ($[170] !== canSummarize || $[171] !== onSummarize || $[172] !== summarizeThreadEnabled || $[173] !== summarizing) {
    t75 = summarizeThreadEnabled && onSummarize && <button className="rounded-md border border-foreground/30 bg-white/60 px-3 py-1 text-[11px] text-foreground hover:bg-white/80 disabled:opacity-60 transition-colors" onClick={onSummarize} disabled={!canSummarize || summarizing} title="Summarize this thread and add summary as a message">{summarizing ? "Summarizing\u2026" : "Summarize thread"}</button>;
    $[170] = canSummarize;
    $[171] = onSummarize;
    $[172] = summarizeThreadEnabled;
    $[173] = summarizing;
    $[174] = t75;
  } else {
    t75 = $[174];
  }
  let t76;
  if ($[175] !== canSummarize || $[176] !== onSummarizeAndContinue || $[177] !== summarizeAndContinueEnabled || $[178] !== summarizing || $[179] !== summarizingAndContinuing) {
    t76 = summarizeAndContinueEnabled && onSummarizeAndContinue && <button className="rounded-md border border-secondary-mint bg-secondary-mint/20 px-3 py-1 text-[11px] text-emerald-700 hover:bg-secondary-mint/40 disabled:opacity-60 transition-all" onClick={onSummarizeAndContinue} disabled={!canSummarize || summarizing || summarizingAndContinuing} title="Summarize this thread and start a new thread with the summary as context">{summarizingAndContinuing ? <span className="flex items-center gap-1.5"><svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Creating new thread…</span> : "Summarize & Continue"}</button>;
    $[175] = canSummarize;
    $[176] = onSummarizeAndContinue;
    $[177] = summarizeAndContinueEnabled;
    $[178] = summarizing;
    $[179] = summarizingAndContinuing;
    $[180] = t76;
  } else {
    t76 = $[180];
  }
  let t77;
  if ($[181] !== canSummarize || $[182] !== forkThreadEnabled || $[183] !== forking || $[184] !== onFork) {
    t77 = forkThreadEnabled && onFork && <button className="rounded-md border border-secondary-pink bg-secondary-pink/20 px-3 py-1 text-[11px] text-pink-700 hover:bg-secondary-pink/40 disabled:opacity-60 transition-all" onClick={onFork} disabled={!canSummarize || forking} title="Fork this thread - create a duplicate with all messages to explore different paths">{forking ? <span className="flex items-center gap-1.5"><svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Forking…</span> : "Fork Thread"}</button>;
    $[181] = canSummarize;
    $[182] = forkThreadEnabled;
    $[183] = forking;
    $[184] = onFork;
    $[185] = t77;
  } else {
    t77 = $[185];
  }
  let t78;
  if ($[186] !== attachedFiles.length || $[187] !== disabled || $[188] !== value) {
    t78 = disabled || !value.trim() && attachedFiles.length === 0;
    $[186] = attachedFiles.length;
    $[187] = disabled;
    $[188] = value;
    $[189] = t78;
  } else {
    t78 = $[189];
  }
  let t79;
  if ($[190] !== onSend || $[191] !== t78) {
    t79 = <button className="rounded-full rainbow-gradient border border-foreground/30 px-5 py-2 text-sm font-medium disabled:opacity-60" onClick={onSend} disabled={t78}>Send</button>;
    $[190] = onSend;
    $[191] = t78;
    $[192] = t79;
  } else {
    t79 = $[192];
  }
  let t80;
  if ($[193] !== t75 || $[194] !== t76 || $[195] !== t77 || $[196] !== t79) {
    t80 = <div className="flex flex-col gap-2">{t75}{t76}{t77}{t79}</div>;
    $[193] = t75;
    $[194] = t76;
    $[195] = t77;
    $[196] = t79;
    $[197] = t80;
  } else {
    t80 = $[197];
  }
  let t81;
  if ($[198] !== t74 || $[199] !== t80) {
    t81 = <div className="flex items-end gap-2">{t74}{t80}</div>;
    $[198] = t74;
    $[199] = t80;
    $[200] = t81;
  } else {
    t81 = $[200];
  }
  let t82;
  if ($[201] !== t55 || $[202] !== t56 || $[203] !== t57 || $[204] !== t81) {
    t82 = <div className="border-t border-white/30 bg-white/40 backdrop-blur-md px-4 py-3">{t55}{t56}{t57}{t81}</div>;
    $[201] = t55;
    $[202] = t56;
    $[203] = t57;
    $[204] = t81;
    $[205] = t82;
  } else {
    t82 = $[205];
  }
  return t82;
};
export default MessageComposer;
function _temp() {
  console.log("[Auto Voice] User stopped speaking");
}
function _temp2(bytes) {
  if (bytes < 1024) {
    return bytes + " B";
  }
  if (bytes < 1048576) {
    return (bytes / 1024).toFixed(1) + " KB";
  }
  return (bytes / 1048576).toFixed(1) + " MB";
}
function _temp3(error, index_0) {
  return <span key={index_0} className="text-red-300">• {error}</span>;
}